import { Injectable,inject } from '@angular/core';
import { Router, Route } from '@angular/router';
import { MegaMenuItem } from 'primeng/api';
import { CoreService, MessageType, UtilsService } from 'src/app/common';
import { InMemoryCacheService } from 'src/app/common/cache/in-memory-cache.service';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { DbcService } from '../../digital-business-card/service/dbc.service';

// Import all interfaces from the centralized interfaces file
import {
  AdminRouteConfig,
  CardDesign,
  DBCOrder,
    OrderResponse,
    PagedList,
  User,
  ApiResponse,
  AdminStats,
  OrderStatus,
  AdminOrganization,
  UserDeletionResult,
  UserDeletionOperationsPage,
  AdminOrganizationMember,
  BillingOwnerTransferResult,
  AdminOrganizationAuditLog,
  AdminOrganizationAuditDetail,
  AdminUsageSummary,
  AdminUsageOperationsPage
} from '../config';

/**
 * Admin Panel Service
 *
 * ✅ User Management:
 *    - getUserCount(): Get total user count for dashboard
 *    - getUsers(): Get all users with billing information
 *    - getBillingDetails(): Get billing details for specific users
 *
 * ✅ Dynamic Menu Generation:
 *    - getAdminMenuItems(): Auto-extract routes from Router config
 *    - getMenuItemsFromRouteConfig(): Convert explicit route config to menu items
 *    - Automatic icon mapping and fallback support
 *
 * ✅ Error Handling:
 *    - Centralized error handling for all admin operations
 *    - Consistent error messaging and logging
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class AdminPanelService {
    private dbcOrderList: DBCOrder[] = [];
    /** Reactive subject holding the current list of DBC orders */
    private dbcOrdersSubject = new BehaviorSubject<DBCOrder[]>([]);
    /** Public observable stream for components to subscribe */
    public dbcOrders$ = this.dbcOrdersSubject.asObservable();
    /** Track if orders have been loaded at least once (for memoization) */
    private dbcOrdersLoaded = false;
    /** In-flight load promise to coalesce concurrent requests */
    private dbcOrdersLoadPromise: Promise<ApiResponse<DBCOrder[]>> | null = null;
    /** user */
    private userList: User[] = [];
    /** Reactive subject holding the current list of users */
    private usersSubject = new BehaviorSubject<User[]>([]);
    /** Public observable stream for components */
    public users$ = this.usersSubject.asObservable();
    /** Track if users have been loaded at least once */
    private usersLoaded = false;
    /** In-flight users load promise to prevent duplicate simultaneous HTTP calls */
    private usersLoadPromise: Promise<ApiResponse<User[]>> | null = null;

    /**
     * Total user count for dashboard
     */
    private userCount: number = 0;
    private userCountLoaded = false;
    private userCountLoadPromise: Promise<ApiResponse<number>> | null = null;

    /**
     * Loading states
     */
    private isLoadingOrders: boolean = false;
    private isLoadingUsers: boolean = false;

    // Pagination state for orders (new paged API)
    private pagedOrdersSubject = new BehaviorSubject<OrderResponse[]>([]);
    public pagedOrders$ = this.pagedOrdersSubject.asObservable();
    private ordersTotalCountSubject = new BehaviorSubject<number>(0);
    public ordersTotalCount$ = this.ordersTotalCountSubject.asObservable();
    private currentPage = 1;
    private pageSize = 10;
    private lastFilters: { orderId?: string; userId?: string; status?: string } = {};

    // Paged Users state (server-side pagination support)
    private pagedUsersSubject = new BehaviorSubject<User[]>([]);
    public pagedUsers$ = this.pagedUsersSubject.asObservable();
    private usersTotalCountSubject = new BehaviorSubject<number>(0);
    public usersTotalCount$ = this.usersTotalCountSubject.asObservable();
    private usersCurrentPage = 1;
    private usersPageSize = 10;

    /** Organization state for the admin panel */
    private organizationList: AdminOrganization[] = [];
    private organizationsSubject = new BehaviorSubject<AdminOrganization[]>([]);
    public organizations$ = this.organizationsSubject.asObservable();
    private organizationsLoaded = false;
    private organizationsLoadPromise: Promise<ApiResponse<AdminOrganization[]>> | null = null;
    private pagedOrganizationsSubject = new BehaviorSubject<AdminOrganization[]>([]);
    public pagedOrganizations$ = this.pagedOrganizationsSubject.asObservable();
    private organizationsTotalCountSubject = new BehaviorSubject<number>(0);
    public organizationsTotalCount$ = this.organizationsTotalCountSubject.asObservable();
    private organizationsCurrentPage = 1;
    private organizationsPageSize = 100;
    private organizationMembersCache = new Map<number, AdminOrganizationMember[]>();
    private organizationAuditCache = new Map<string, AdminOrganizationAuditLog[]>();
    private organizationAuditDetailsCache = new Map<string, AdminOrganizationAuditDetail>();

    public dbcService = inject(DbcService);
    private availableProducts: any[] = [];
    private productsLoadPromise: Promise<any[]> | null = null;
    // ============================================================================
    // GETTERS - Access to state from components
    // ============================================================================

    public get orders(): DBCOrder[] {
        return this.dbcOrderList;
    }

    public get users(): User[] {
        return this.userList;
    }

    public get organizations(): AdminOrganization[] {
        return this.organizationList;
    }

    public get totalOrganizationsCount(): number {
        return this.organizationsTotalCountSubject.value;
    }

    public get currentOrganizationsPage(): number {
        return this.organizationsCurrentPage;
    }

    public get currentOrganizationsPageSize(): number {
        return this.organizationsPageSize;
    }

    public get totalUsers(): number {
        return this.userCount;
    }

    public get isOrdersLoading(): boolean {
        return this.isLoadingOrders;
    }

    public get totalOrdersCount(): number { return this.ordersTotalCountSubject.value; }
    public get currentOrdersPage(): number { return this.currentPage; }
    public get currentOrdersPageSize(): number { return this.pageSize; }

    public get isUsersLoading(): boolean {
        return this.isLoadingUsers;
    }

    constructor(
        private core: CoreService<{}>,
        private utils: UtilsService,
        private router: Router,
        private cache: InMemoryCacheService
    ) { }

    /** Lazy-load products*/
    public async ensureProductsLoaded(force: boolean = false): Promise<any[]> {
        if (this.availableProducts.length && !force) return this.availableProducts;
        if (this.productsLoadPromise && !force) return this.productsLoadPromise;
        this.productsLoadPromise = this.dbcService.handleGetProductsAvailable()
            .then(list => this.availableProducts = list || [])
            .catch(err => { throw err; })
            .finally(() => { this.productsLoadPromise = null; });
        return this.productsLoadPromise;
    }

    /** Public accessor for components/services */
    public getAvailableProducts(options: { force?: boolean } = {}): Promise<any[]> {
        return this.ensureProductsLoaded(!!options.force);
    }
    /**
     * Get total user count for dashboard
     * @param handleError Whether to show error messages to user
     * @returns Promise with response data and error
     */
    public async getUserCount(handleError: boolean = false, options: { force?: boolean } = {}): Promise<ApiResponse<number>> {
        const { force = false } = options;
        if (this.userCountLoaded && !force) {
            return { data: this.userCount, error: null, success: true } as ApiResponse<number>;
        }
        if (this.userCountLoadPromise && !force) {
            return this.userCountLoadPromise;
        }
        const exec = async (): Promise<ApiResponse<number>> => {
            const response: ApiResponse<number> = { data: null, error: null };
            this.cache.loading = true;
            try {
                // If users already loaded we can derive count without API call
                if (this.usersLoaded && !force) {
                    this.userCount = this.userList.length;
                } else {
                    const apiResponse = await firstValueFrom(this.core.get(`${environment.identityUrl}/user/count`));
                    this.userCount = apiResponse as number;
                }
                response.data = this.userCount;
                response.success = true;
                this.userCountLoaded = true;
            } catch (error) {
                response.error = error;
                response.success = false;
                await this.handleError(error, handleError);
            } finally {
                this.cache.loading = false;
                this.userCountLoadPromise = null;
            }
            return response;
        };
        this.userCountLoadPromise = exec();
        return this.userCountLoadPromise;
    }

    /**
     * Get users from API and update internal state
     * @param handleError Whether to show error messages to user
     * @returns Promise with response data and error
     */
    /**
     * (Deprecated – prefer loadUsersIfNeeded / users$) Fetch users (cached by default).
     * Retained to avoid breaking existing callers. Uses cached load (no force).
     */
    public async getUsers(handleError: boolean = false): Promise<ApiResponse<User[]>> {
        return this.loadUsersIfNeeded({ handleError });
    }

    /**
     * Load users only once unless force=true. Coalesces concurrent calls.
     */
    public async loadUsersIfNeeded(options: { force?: boolean; handleError?: boolean } = {}): Promise<ApiResponse<User[]>> {
        const { force = false, handleError = false } = options;
        if (this.usersLoaded && !force) {
            return { data: this.usersSubject.value, error: null, success: true } as ApiResponse<User[]>;
        }
        if (this.usersLoadPromise && !force) {
            return this.usersLoadPromise;
        }
        const response: ApiResponse<User[]> = { data: null, error: null };
        this.isLoadingUsers = true;
        this.cache.loading = true;
        this.usersLoadPromise = (async () => {
            try {
                const raw = await firstValueFrom(this.core.get(`${environment.identityUrl}/user`));
                const users = (Array.isArray(raw) ? raw : (raw && raw['data']) || []) as User[];
                this.userList = users;
                this.usersSubject.next(this.userList);
                this.usersLoaded = true;
                this.userCount = this.userList.length; // derive count
                this.userCountLoaded = true;
                // If orders already loaded but lack user details, reattach (and product details if available)
                if (this.dbcOrderList.length) {
                    this.dbcOrderList = this.attachUserDetailsToOrders(this.dbcOrderList, this.userList);
                    if (this.availableProducts.length) {
                        this.attachProductDetailsToOrders(this.dbcOrderList)
                            .then(updated => {
                                this.dbcOrderList = updated;
                                this.dbcOrdersSubject.next(this.dbcOrderList);
                            })
                            .catch(() => this.dbcOrdersSubject.next(this.dbcOrderList));
                    } else {
                        this.dbcOrdersSubject.next(this.dbcOrderList);
                    }
                }
                response.data = this.userList;
                response.success = true;
            } catch (error) {
                response.error = error;
                response.success = false;
                await this.handleError(error, handleError);
            } finally {
                this.cache.loading = false;
                this.isLoadingUsers = false;
                this.usersLoadPromise = null;
            }
            return response;
        })();
        this.utils.logger.info("Order List", this.dbcOrderList);
        return this.usersLoadPromise;
    }

    /** Force refresh users (bypass cache) */
    public refreshUsers(handleError: boolean = false) {
        return this.loadUsersIfNeeded({ force: true, handleError });
    }

    /** Get all organizations for the admin panel. */
    public async getOrganizations(options: { force?: boolean; handleError?: boolean } = {}): Promise<ApiResponse<AdminOrganization[]>> {
        const { force = false, handleError = false } = options;

        if (this.organizationsLoaded && !force) {
            return { data: this.organizationsSubject.value, error: null, success: true } as ApiResponse<AdminOrganization[]>;
        }

        if (this.organizationsLoadPromise && !force) {
            return this.organizationsLoadPromise;
        }

        this.organizationsLoadPromise = (async (): Promise<ApiResponse<AdminOrganization[]>> => {
            const response: ApiResponse<AdminOrganization[]> = { data: null, error: null };
            this.cache.loading = true;

            try {
                const raw = await firstValueFrom(this.core.get(`${environment.identityUrl}/Organization`));
                const organizations = this.normalizeOrganizations(raw);
                this.organizationList = organizations;
                this.organizationsSubject.next([...organizations]);
                this.organizationsLoaded = true;
                response.data = organizations;
                response.success = true;
            } catch (error) {
                response.error = error;
                response.success = false;
                await this.handleError(error, handleError);
            } finally {
                this.cache.loading = false;
                this.organizationsLoadPromise = null;
            }

            return response;
        })();

        return this.organizationsLoadPromise;
    }

    /** Get organizations for the admin panel with server-side pagination and deleted records included. */
    public async getPagedOrganizations(options: { page?: number; pageSize?: number; q?: string; force?: boolean; handleError?: boolean } = {}): Promise<ApiResponse<PagedList<AdminOrganization>>> {
        const {
            page = this.organizationsCurrentPage,
            pageSize = this.organizationsPageSize,
            q = '',
            force = false,
            handleError = false
        } = options;

        if (
            this.organizationsLoaded &&
            !force &&
            page === this.organizationsCurrentPage &&
            pageSize === this.organizationsPageSize
        ) {
            return {
                data: {
                    items: this.pagedOrganizationsSubject.value,
                    pageNumber: this.organizationsCurrentPage,
                    pageSize: this.organizationsPageSize,
                    totalCount: this.organizationsTotalCountSubject.value,
                    totalPages: Math.ceil(this.organizationsTotalCountSubject.value / this.organizationsPageSize),
                    hasNext: (this.organizationsCurrentPage * this.organizationsPageSize) < this.organizationsTotalCountSubject.value
                },
                error: null,
                success: true
            } as ApiResponse<PagedList<AdminOrganization>>;
        }

        const response: ApiResponse<PagedList<AdminOrganization>> = { data: null, error: null };
        this.cache.loading = true;

        try {
            const url = `${environment.identityUrl}/Organization?includeDeleted=true&page=${page}&pageSize=${pageSize}&q=${encodeURIComponent(q)}`;
            const raw = await firstValueFrom(this.core.get(url));
            const paged = this.normalizePagedOrganizations(raw, page, pageSize);
            this.organizationList = paged.items;
            this.organizationsSubject.next([...paged.items]);
            this.pagedOrganizationsSubject.next([...paged.items]);
            this.organizationsTotalCountSubject.next(paged.totalCount);
            this.organizationsCurrentPage = paged.pageNumber;
            this.organizationsPageSize = paged.pageSize;
            this.organizationsLoaded = true;
            response.data = paged;
            response.totalCount = paged.totalCount;
            response.success = true;
        } catch (error) {
            response.error = error;
            response.success = false;
            await this.handleError(error, handleError);
        } finally {
            this.cache.loading = false;
        }

        return response;
    }

    public async getAdminUsageSummary(
        orgId: number,
        options: { handleError?: boolean } = {}
    ): Promise<ApiResponse<AdminUsageSummary>> {
        const response: ApiResponse<AdminUsageSummary> = { data: null, error: null };
        if (!orgId) {
            response.error = 'Missing orgId';
            response.success = false;
            return response;
        }

        try {
            response.data = await firstValueFrom(
                this.core.get(`${environment.seoUrl}/admin-usage/organizations/${orgId}/summary`)
            ) as AdminUsageSummary;
            response.success = true;
        } catch (error) {
            response.error = error;
            response.success = false;
            await this.handleError(error, options.handleError ?? false);
        }

        return response;
    }

    public async getAdminUsageOperations(
        orgId: number,
        options: {
            page?: number;
            pageSize?: number;
            operationKey?: string;
            state?: string;
            provider?: string;
            cacheStatus?: string;
            handleError?: boolean;
        } = {}
    ): Promise<ApiResponse<AdminUsageOperationsPage>> {
        const response: ApiResponse<AdminUsageOperationsPage> = { data: null, error: null };
        if (!orgId) {
            response.error = 'Missing orgId';
            response.success = false;
            return response;
        }

        const params = new URLSearchParams();
        params.set('page', String(options.page ?? 1));
        params.set('pageSize', String(options.pageSize ?? 10));
        if (options.operationKey?.trim()) params.set('operationKey', options.operationKey.trim());
        if (options.state?.trim()) params.set('state', options.state.trim());
        if (options.provider?.trim()) params.set('provider', options.provider.trim());
        if (options.cacheStatus?.trim()) params.set('cacheStatus', options.cacheStatus.trim());

        try {
            response.data = await firstValueFrom(
                this.core.get(`${environment.seoUrl}/admin-usage/organizations/${orgId}/operations?${params.toString()}`)
            ) as AdminUsageOperationsPage;
            response.totalCount = response.data.totalCount;
            response.success = true;
        } catch (error) {
            response.error = error;
            response.success = false;
            await this.handleError(error, options.handleError ?? false);
        }

        return response;
    }

    /** Fetch members for a specific organization. */
    public async getOrganizationMembers(orgId: number, options: { force?: boolean; handleError?: boolean; q?: string; page?: number; pageSize?: number } = {}): Promise<ApiResponse<AdminOrganizationMember[]>> {
        const { force = false, handleError = false, q = '', page = 1, pageSize = 10 } = options;
        const response: ApiResponse<AdminOrganizationMember[]> = { data: null, error: null };

        if (!orgId) {
            response.error = 'Missing orgId';
            response.success = false;
            return response;
        }

        if (q === '' && page === 1 && pageSize === 100 && this.organizationMembersCache.has(orgId) && !force) {
            response.data = this.organizationMembersCache.get(orgId) ?? [];
            response.totalCount = response.data.length;
            response.success = true;
            return response;
        }

        this.cache.loading = true;
        try {
            const url = `${environment.identityUrl}/Organization/${orgId}/members?q=${encodeURIComponent(q)}&page=${page}&pageSize=${pageSize}`;
            const raw = await firstValueFrom(this.core.get(url));
            const normalized = this.normalizeOrganizationMembers(raw);
            const members = normalized.items;
            response.data = members;
            response.totalCount = normalized.totalCount ?? members.length;
            response.success = true;

            if (q === '' && page === 1 && pageSize === 100) {
                this.organizationMembersCache.set(orgId, members);
            }
        } catch (error) {
            response.error = error;
            response.success = false;
            await this.handleError(error, handleError);
        } finally {
            this.cache.loading = false;
        }

        return response;
    }

    public async transferOrganizationBillingOwner(
        organizationId: number,
        newBillingOwnerUserId: string,
        reason: string,
        options: { handleError?: boolean } = {}
    ): Promise<ApiResponse<BillingOwnerTransferResult>> {
        const response: ApiResponse<BillingOwnerTransferResult> = { data: null, error: null };
        if (!organizationId || !newBillingOwnerUserId?.trim() || reason.trim().length < 3) {
            response.error = 'Organization, replacement billing owner, and reason are required';
            response.success = false;
            return response;
        }

        try {
            response.data = await firstValueFrom(this.core.put(
                `${environment.identityUrl}/Organization/${organizationId}/billing-owner`,
                {
                    newBillingOwnerUserId: newBillingOwnerUserId.trim(),
                    reason: reason.trim()
                }
            )) as BillingOwnerTransferResult;
            response.success = true;
            this.organizationMembersCache.delete(organizationId);
            this.organizationsLoaded = false;
        } catch (error) {
            response.error = error;
            response.success = false;
            await this.handleError(error, options.handleError ?? true);
        }

        return response;
    }

    /** Fetch organization audit logs for the admin panel organization members view. */
    public async getOrganizationAuditLogs(
        orgId: number,
        options: { force?: boolean; handleError?: boolean; page?: number; pageSize?: number } = {}
    ): Promise<ApiResponse<AdminOrganizationAuditLog[]>> {
        const { force = false, handleError = false, page = 1, pageSize = 10 } = options;
        const response: ApiResponse<AdminOrganizationAuditLog[]> = { data: null, error: null };

        if (!orgId) {
            response.error = 'Missing orgId';
            response.success = false;
            return response;
        }

        const cacheKey = `${orgId}:${page}:${pageSize}`;
        if (!force && this.organizationAuditCache.has(cacheKey)) {
            response.data = this.organizationAuditCache.get(cacheKey) ?? [];
            response.totalCount = response.data.length;
            response.success = true;
            return response;
        }

        this.cache.loading = true;
        try {
            const url = `${environment.identityUrl}/organizations/${orgId}/audit?page=${page}&pageSize=${pageSize}`;
            const raw = await firstValueFrom(this.core.get(url));
            const normalized = this.normalizeOrganizationAuditLogs(raw);
            response.data = normalized.items;
            response.totalCount = normalized.totalCount ?? normalized.items.length;
            response.success = true;
            this.organizationAuditCache.set(cacheKey, normalized.items);
        } catch (error) {
            response.error = error;
            response.success = false;
            await this.handleError(error, handleError);
        } finally {
            this.cache.loading = false;
        }

        return response;
    }

    /** Fetch a single organization audit entry by id for the admin panel organization members view. */
    public async getOrganizationAuditLogDetail(
        orgId: number,
        auditId: string,
        options: { force?: boolean; handleError?: boolean } = {}
    ): Promise<ApiResponse<AdminOrganizationAuditDetail>> {
        const { force = false, handleError = false } = options;
        const response: ApiResponse<AdminOrganizationAuditDetail> = { data: null, error: null };

        if (!orgId || !auditId) {
            response.error = 'Missing orgId or auditId';
            response.success = false;
            return response;
        }

        const cacheKey = `${orgId}:${auditId}`;
        if (!force && this.organizationAuditDetailsCache.has(cacheKey)) {
            response.data = this.organizationAuditDetailsCache.get(cacheKey) ?? null;
            response.success = true;
            return response;
        }

        this.cache.loading = true;
        try {
            const url = `${environment.identityUrl}/organizations/${orgId}/audit/${encodeURIComponent(auditId)}`;
            const raw = await firstValueFrom(this.core.get(url));
            const detail = this.normalizeOrganizationAuditDetail(raw, auditId);
            response.data = detail;
            response.success = true;
            this.organizationAuditDetailsCache.set(cacheKey, detail);
        } catch (error) {
            response.error = error;
            response.success = false;
            await this.handleError(error, handleError);
        } finally {
            this.cache.loading = false;
        }

        return response;
    }

    /** Soft delete an organization while keeping its record for follow-up actions. */
    public async softDeleteOrganization(orgId: number, options: { handleError?: boolean } = {}): Promise<ApiResponse<{ orgId: number }>> {
        const { handleError = true } = options;
        const response: ApiResponse<{ orgId: number }> = { data: null, error: null };

        if (!orgId) {
            response.error = 'Missing orgId';
            response.success = false;
            return response;
        }

        this.cache.loading = true;
        try {
            const url = `${environment.billingServiceUrl}/organization/${orgId}/billing/operations`;
            const mutationResponse = await firstValueFrom(this.core.post(url, { operationId: crypto.randomUUID(), action: 'close-soft', immediate: true, reason: 'Administrator requested organization soft-delete' }));
            this.requireCompletedBillingOperation(mutationResponse);
            const normalizedMutation = this.normalizeOrganizations(mutationResponse).find(org => org.id === orgId);
            const deletedDate = normalizedMutation?.deletedDate ?? new Date().toISOString();
            const deletedByUserId = normalizedMutation?.deletedByUserId ?? null;
            this.organizationList = this.organizationList.map(org =>
                org.id === orgId
                    ? {
                        ...org,
                        ...normalizedMutation,
                        isDeleted: true,
                        deletedDate,
                        deletedByUserId,
                        status: 'Deleted'
                    }
                    : org
            );
            this.organizationsSubject.next([...this.organizationList]);
            response.data = { orgId };
            response.success = true;
        } catch (error) {
            response.error = error;
            response.success = false;
            await this.handleError(error, handleError);
        } finally {
            this.cache.loading = false;
        }

        return response;
    }

    /** Restore a previously soft deleted organization from the admin panel. */
    public async restoreOrganization(orgId: number, options: { handleError?: boolean } = {}): Promise<ApiResponse<{ orgId: number }>> {
        const { handleError = true } = options;
        const response: ApiResponse<{ orgId: number }> = { data: null, error: null };

        if (!orgId) {
            response.error = 'Missing orgId';
            response.success = false;
            return response;
        }

        this.cache.loading = true;
        try {
            const url = `${environment.billingServiceUrl}/organization/${orgId}/billing/operations`;
            const mutationResponse = await firstValueFrom(this.core.post(url, { operationId: crypto.randomUUID(), action: 'restore', immediate: true, reason: 'Administrator requested organization restore' }));
            this.requireCompletedBillingOperation(mutationResponse);
            const normalizedMutation = this.normalizeOrganizations(mutationResponse).find(org => org.id === orgId);
            this.organizationList = this.organizationList.map(org =>
                org.id === orgId
                    ? {
                        ...org,
                        ...normalizedMutation,
                        isDeleted: false,
                        deletedDate: null,
                        deletedByUserId: null,
                        status: 'Active'
                    }
                    : org
            );
            this.organizationsSubject.next([...this.organizationList]);
            response.data = { orgId };
            response.success = true;
        } catch (error) {
            response.error = error;
            response.success = false;
            await this.handleError(error, handleError);
        } finally {
            this.cache.loading = false;
        }

        return response;
    }

    /** Permanently delete an organization from the admin panel. */
    public async hardDeleteOrganization(orgId: number, options: { handleError?: boolean } = {}): Promise<ApiResponse<{ orgId: number }>> {
        const { handleError = true } = options;
        const response: ApiResponse<{ orgId: number }> = { data: null, error: null };

        if (!orgId) {
            response.error = 'Missing orgId';
            response.success = false;
            return response;
        }

        this.cache.loading = true;
        try {
            const url = `${environment.billingServiceUrl}/organization/${orgId}/billing/operations`;
            const result = await firstValueFrom(this.core.post(url, { operationId: crypto.randomUUID(), action: 'close-hard', immediate: true, reason: 'Administrator requested permanent organization deletion' }));
            this.requireCompletedBillingOperation(result);
            this.organizationList = this.organizationList.filter(org => org.id !== orgId);
            this.organizationsSubject.next([...this.organizationList]);
            this.organizationMembersCache.delete(orgId);
            response.data = { orgId };
            response.success = true;
        } catch (error) {
            response.error = error;
            response.success = false;
            await this.handleError(error, handleError);
        } finally {
            this.cache.loading = false;
        }

        return response;
    }

    /** Permanently remove a member from an organization using the admin hard-delete endpoint. */
    public async hardDeleteOrganizationUser(orgId: number, userId: string, options: { handleError?: boolean } = {}): Promise<ApiResponse<{ orgId: number; userId: string }>> {
        const { handleError = true } = options;
        const response: ApiResponse<{ orgId: number; userId: string }> = { data: null, error: null };

        if (!orgId || !userId) {
            response.error = 'Missing orgId or userId';
            response.success = false;
            return response;
        }

        this.cache.loading = true;
        try {
            const url = `${environment.identityUrl}/organization/${orgId}/user/${userId}/hard-delete`;
            await firstValueFrom(this.core.post(url, {}));
            const currentMembers = this.organizationMembersCache.get(orgId) ?? [];
            this.organizationMembersCache.set(orgId, currentMembers.filter(member => member.userId !== userId));
            response.data = { orgId, userId };
            response.success = true;
        } catch (error) {
            response.error = error;
            response.success = false;
            await this.handleError(error, handleError);
        } finally {
            this.cache.loading = false;
        }

        return response;
    }

    /**
     * Get billing details for a specific user
     * @param billingID The billing ID to fetch details for
     * @returns Promise with billing data
     */
    public async getBillingDetails(billingID: string) {
        const response = { data: null, error: null };

        this.cache.loading = true;
        try {
            response.data = await firstValueFrom(this.core.get(`${environment.identityUrl}/billingProduct/billingUser/${billingID}`));
        } catch (error) {
            response.data = 'No data';
        } finally {
            this.cache.loading = false;
        }
        return response;
    }

    /**
     * Get DBC Orders from API and update internal state
     * @param handleError Whether to show error messages to user
     * @returns Promise with response data and error
     */
    /**
     * (Deprecated – use loadDBCOrdersIfNeeded / dbcOrders$) Fetch orders from API.
     * Retained for backward compatibility with existing callers.
     */
    public async getDBCOrders(handleError: boolean = false): Promise<ApiResponse<DBCOrder[]>> {
        return this.loadDBCOrdersIfNeeded({ force: true, handleError });
    }
    /**
     * Load DBC orders only once (unless force = true). Provides a single shared
     * promise so multiple callers while loading don't trigger duplicate HTTP requests.
     * Updates both internal array state and reactive BehaviorSubject.
     */
    public async loadDBCOrdersIfNeeded(options: { force?: boolean; handleError?: boolean } = {}): Promise<ApiResponse<DBCOrder[]>> {
        const { force = false, handleError = false } = options;
        // Already loaded and not forced: return cached data
        if (this.dbcOrdersLoaded && !force) {
            return { data: this.dbcOrdersSubject.value, error: null, success: true } as ApiResponse<DBCOrder[]>;
        }
        // If a load is already in flight, return that promise
        if (this.dbcOrdersLoadPromise && !force) {
            return this.dbcOrdersLoadPromise;
        }

        const response: ApiResponse<DBCOrder[]> = { data: null, error: null };
        this.isLoadingOrders = true;
        this.cache.loading = true;
        this.dbcOrdersLoadPromise = (async () => {
            try {
                const raw = await firstValueFrom(this.core.get(`${environment.billingServiceUrl}/Order`));
                // Normalize possible response shapes
                response.data = this.dbcOrderList;
                response.success = true;
                this.dbcOrderList = this.normalizeOrdersResponse(raw);
                this.dbcOrderList = this.attachUserDetailsToOrders(this.dbcOrderList, this.userList);
                try {
                    this.dbcOrderList = await this.attachProductDetailsToOrders(this.dbcOrderList);
                } catch (e) {
                    this.utils.logger.warn('Product detail attachment failed (non-fatal)', e);
                }
                // Ensure QR info is convenient for UI consumption
                this.dbcOrderList = this.attachQrDetailsToOrders(this.dbcOrderList);
                this.dbcOrdersSubject.next(this.dbcOrderList);
                this.dbcOrdersLoaded = true;
            } catch (error) {
                response.error = error;
                response.success = false;
                await this.handleError(error, handleError);
            } finally {
                this.cache.loading = false;
                this.isLoadingOrders = false;
                this.dbcOrdersLoadPromise = null; // allow future forced reloads
            }
            return response;
        })();
        return this.dbcOrdersLoadPromise;
    }

    /**
     * Fetch orders from API applying optional filters. Does not cache the result
     * so that clearing filters easily restores the full cached list via
     * loadDBCOrdersIfNeeded().
     * @param filters orderId, userId, status (all optional)
     */
    public async filterDBCOrders(filters: { orderId?: string; userId?: string; status?: string; handleError?: boolean } = {}): Promise<ApiResponse<DBCOrder[]>> {
        const { orderId, userId, status, handleError = false } = filters;
        const response: ApiResponse<DBCOrder[]> = { data: null, error: null };
        const params = new URLSearchParams();
        if (orderId) params.set('orderId', orderId);
        if (userId) params.set('userId', userId);
        if (status) params.set('status', status);
        let url = `${environment.billingServiceUrl}/Order`;
        const qs = params.toString();
        if (qs) url += `?${qs}`;
        this.isLoadingOrders = true;
        this.cache.loading = true;
        try {
            const raw = await firstValueFrom(this.core.get(url));
            let list = this.normalizeOrdersResponse(raw);
            list = this.attachUserDetailsToOrders(list, this.userList);
            try {
                list = await this.attachProductDetailsToOrders(list);
            } catch (e) {
                this.utils.logger.warn('Product detail attachment failed during filtered fetch (non-fatal)', e);
            }
            // Map QR details for UI
            list = this.attachQrDetailsToOrders(list);
            this.dbcOrderList = list; // keep current view consistent
            this.dbcOrdersSubject.next(this.dbcOrderList);
            response.data = list;
            response.success = true;
        } catch (error) {
            response.error = error;
            response.success = false;
            await this.handleError(error, handleError);
        } finally {
            this.isLoadingOrders = false;
        }
        return response;
    }

    /** Fetch a single page of orders from backend paged endpoint */
    public async getPagedOrders(options: { page?: number; pageSize?: number; orderId?: string; userId?: string; status?: string; force?: boolean; handleError?: boolean } = {}): Promise<ApiResponse<PagedList<OrderResponse>>> {
        const { page = this.currentPage, pageSize = this.pageSize, orderId, userId, status, force = false, handleError = false } = options;
        const response: ApiResponse<PagedList<OrderResponse>> = { data: null, error: null };
        // Avoid duplicate call if same params and already loading
        const sameFilters = JSON.stringify({ page, pageSize, orderId, userId, status }) === JSON.stringify({ page: this.currentPage, pageSize: this.pageSize, ...this.lastFilters });
        if (this.isLoadingOrders && sameFilters && !force) {
            return { data: { items: this.pagedOrdersSubject.value, pageNumber: this.currentPage, pageSize: this.pageSize, totalCount: this.ordersTotalCountSubject.value, hasNext: (this.currentPage * this.pageSize) < this.ordersTotalCountSubject.value }, error: null, success: true } as ApiResponse<PagedList<OrderResponse>>;
        }
        this.isLoadingOrders = true;
        this.cache.loading = true;
        try {
            const params = new URLSearchParams();
            params.set('PageNumber', String(page));
            params.set('PageSize', String(pageSize));
            if (orderId) params.set('orderId', orderId);
            if (userId) params.set('userId', userId);
            if (status) params.set('status', status);
            const url = `${environment.billingServiceUrl}/Order?${params.toString()}`;
            const httpRes = await firstValueFrom(this.core.getResponse(url));
            const rawBody: any = httpRes?.body;
            // Expect backend shape matching PagedList<OrderResponse>
            const paged = rawBody as PagedList<OrderResponse>;
            // Fallback normalization if backend returns different keys
            const items = (paged?.items || (paged as any)?.data || (Array.isArray(rawBody) ? rawBody : [])) as OrderResponse[];
            // Decorate with user & product details (if not already included)
            let enriched: any[] = this.attachUserDetailsToOrders(items as any, this.userList);
            try { enriched = await this.attachProductDetailsToOrders(enriched as any); } catch { /* ignore */ }
            // Add qrFileUrl mapping for display
            enriched = this.attachQrDetailsToOrders(enriched as any);
            this.pagedOrdersSubject.next(enriched as OrderResponse[]);
            // Prefer pagination metadata from response headers
            const headers = httpRes?.headers;
            const totalHeader = headers?.get('TotalCount') ?? headers?.get('X-Total-Count');
            const sizeHeader = headers?.get('PageSize');
            const currentHeader = headers?.get('CurrentPage');
            const totalPagesHeader = headers?.get('TotalPages');
            const hasNextHeader = headers?.get('HasNext');
            const hasPrevHeader = headers?.get('HasPrevious');
            const xPagination = headers?.get('X-Pagination');

            let totalFromHeader: number | null = null;
            let pageFromHeader: number | null = null;
            let sizeFromHeader: number | null = null;
            let totalPagesFromHeader: number | null = null;
            let hasNextFromHeader: boolean | null = null;
            let hasPrevFromHeader: boolean | null = null;

            if (xPagination) {
                try {
                    const parsed = JSON.parse(xPagination);
                    totalFromHeader = Number(parsed.TotalCount);
                    pageFromHeader = Number(parsed.CurrentPage);
                    sizeFromHeader = Number(parsed.PageSize);
                    totalPagesFromHeader = Number(parsed.TotalPages);
                    hasNextFromHeader = Boolean(parsed.HasNext);
                    hasPrevFromHeader = Boolean(parsed.HasPrevious);
                } catch { /* ignore */ }
            }
            // Individual headers override
            if (totalHeader !== null) totalFromHeader = Number(totalHeader);
            if (sizeHeader !== null) sizeFromHeader = Number(sizeHeader);
            if (currentHeader !== null) pageFromHeader = Number(currentHeader);
            if (totalPagesHeader !== null) totalPagesFromHeader = Number(totalPagesHeader);
            if (hasNextHeader !== null) hasNextFromHeader = String(hasNextHeader).toLowerCase() === 'true';
            if (hasPrevHeader !== null) hasPrevFromHeader = String(hasPrevHeader).toLowerCase() === 'true';

            if (totalFromHeader !== null || sizeFromHeader !== null || pageFromHeader !== null || totalPagesFromHeader !== null) {
                const total = totalFromHeader ?? enriched.length;
                const pg = pageFromHeader ?? page;
                const psz = sizeFromHeader ?? pageSize;
                const tpages = totalPagesFromHeader ?? (psz ? Math.ceil(total / psz) : 0);
                const hnext = hasNextFromHeader ?? ((pg * psz) < total);
                const hprev = hasPrevFromHeader ?? (pg > 1);
                this.ordersTotalCountSubject.next(total);
                this.currentPage = pg;
                this.pageSize = psz;
                response.data = {
                    items: enriched as OrderResponse[],
                    pageNumber: this.currentPage,
                    pageSize: this.pageSize,
                    totalCount: total,
                    hasNext: hnext,
                    hasPrevious: hprev,
                    totalPages: tpages
                };
            } else {
                const total = paged?.totalCount ?? (rawBody as any)?.totalCount ?? enriched.length;
                this.ordersTotalCountSubject.next(total);
                this.currentPage = paged?.pageNumber || page;
                this.pageSize = paged?.pageSize || pageSize;
                response.data = {
                    items: enriched as OrderResponse[],
                    pageNumber: this.currentPage,
                    pageSize: this.pageSize,
                    totalCount: total,
                    hasNext: (this.currentPage * this.pageSize) < total,
                    hasPrevious: this.currentPage > 1,
                    totalPages: Math.ceil(total / this.pageSize)
                };
            }
            this.lastFilters = { orderId, userId, status };
            response.success = true;
        } catch (error) {
            response.error = error;
            response.success = false;
            await this.handleError(error, handleError);
        } finally {
            this.cache.loading = false;
            this.isLoadingOrders = false;
        }
        return response;
    }

    /**
     * Fetch a page of orders filtered by status using dedicated endpoint /Order/status/{status}
     * (Used when user applies only a status filter for server-side accuracy.)
     */
    public async getPagedOrdersByStatus(status: string, options: { page?: number; pageSize?: number; force?: boolean; handleError?: boolean } = {}): Promise<ApiResponse<PagedList<OrderResponse>>> {
        const { page = 1, pageSize = this.pageSize, force = false, handleError = false } = options;
        const response: ApiResponse<PagedList<OrderResponse>> = { data: null, error: null };
        if (!status) {
            response.error = 'Status is required';
            return response;
        }
        this.isLoadingOrders = true;
        this.cache.loading = true;
        try {
            const params = new URLSearchParams();
            params.set('PageNumber', String(page));
            params.set('PageSize', String(pageSize));
            const url = `${environment.billingServiceUrl}/Order/status/${encodeURIComponent(status)}?${params.toString()}`;
            const httpRes = await firstValueFrom(this.core.getResponse(url));
            const rawBody: any = httpRes?.body;
            const paged = rawBody as PagedList<OrderResponse>;
            const items = (paged?.items || (paged as any)?.data || (Array.isArray(rawBody) ? rawBody : [])) as OrderResponse[];
            let enriched: any[] = this.attachUserDetailsToOrders(items as any, this.userList);
            try { enriched = await this.attachProductDetailsToOrders(enriched as any); } catch { /* ignore */ }
            // Add qrFileUrl mapping for display
            enriched = this.attachQrDetailsToOrders(enriched as any);
            this.pagedOrdersSubject.next(enriched as OrderResponse[]);
            // Prefer pagination metadata from response headers
            const headers = httpRes?.headers;
            const totalHeader = headers?.get('TotalCount') ?? headers?.get('X-Total-Count');
            const sizeHeader = headers?.get('PageSize');
            const currentHeader = headers?.get('CurrentPage');
            const totalPagesHeader = headers?.get('TotalPages');
            const hasNextHeader = headers?.get('HasNext');
            const hasPrevHeader = headers?.get('HasPrevious');
            const xPagination = headers?.get('X-Pagination');

            let totalFromHeader: number | null = null;
            let pageFromHeader: number | null = null;
            let sizeFromHeader: number | null = null;
            let totalPagesFromHeader: number | null = null;
            let hasNextFromHeader: boolean | null = null;
            let hasPrevFromHeader: boolean | null = null;

            if (xPagination) {
                try {
                    const parsed = JSON.parse(xPagination);
                    totalFromHeader = Number(parsed.TotalCount);
                    pageFromHeader = Number(parsed.CurrentPage);
                    sizeFromHeader = Number(parsed.PageSize);
                    totalPagesFromHeader = Number(parsed.TotalPages);
                    hasNextFromHeader = Boolean(parsed.HasNext);
                    hasPrevFromHeader = Boolean(parsed.HasPrevious);
                } catch { /* ignore */ }
            }
            if (totalHeader !== null) totalFromHeader = Number(totalHeader);
            if (sizeHeader !== null) sizeFromHeader = Number(sizeHeader);
            if (currentHeader !== null) pageFromHeader = Number(currentHeader);
            if (totalPagesHeader !== null) totalPagesFromHeader = Number(totalPagesHeader);
            if (hasNextHeader !== null) hasNextFromHeader = String(hasNextHeader).toLowerCase() === 'true';
            if (hasPrevHeader !== null) hasPrevFromHeader = String(hasPrevHeader).toLowerCase() === 'true';

            if (totalFromHeader !== null || sizeFromHeader !== null || pageFromHeader !== null || totalPagesFromHeader !== null) {
                const total = totalFromHeader ?? enriched.length;
                const pg = pageFromHeader ?? page;
                const psz = sizeFromHeader ?? pageSize;
                const tpages = totalPagesFromHeader ?? (psz ? Math.ceil(total / psz) : 0);
                const hnext = hasNextFromHeader ?? ((pg * psz) < total);
                const hprev = hasPrevFromHeader ?? (pg > 1);
                this.ordersTotalCountSubject.next(total);
                this.currentPage = pg;
                this.pageSize = psz;
                response.data = {
                    items: enriched as OrderResponse[],
                    pageNumber: this.currentPage,
                    pageSize: this.pageSize,
                    totalCount: total,
                    hasNext: hnext,
                    hasPrevious: hprev,
                    totalPages: tpages
                };
            } else {
                const total = paged?.totalCount ?? (rawBody as any)?.totalCount ?? enriched.length;
                this.ordersTotalCountSubject.next(total);
                this.currentPage = page;
                this.pageSize = pageSize;
                response.data = {
                    items: enriched as OrderResponse[],
                    pageNumber: this.currentPage,
                    pageSize: this.pageSize,
                    totalCount: total,
                    hasNext: (this.currentPage * this.pageSize) < total,
                    hasPrevious: this.currentPage > 1,
                    totalPages: Math.ceil(total / this.pageSize)
                };
            }
            this.lastFilters = { status };
            response.success = true;
        } catch (error) {
            response.error = error;
            response.success = false;
            await this.handleError(error, handleError);
        } finally {
            this.cache.loading = false;
            this.isLoadingOrders = false;
        }
        return response;
    }

    /** Fetch a page of users from backend paged endpoint (hybrid support) */
    public async getPagedUsers(options: {
        page?: number;
        pageSize?: number;
        force?: boolean;
        handleError?: boolean;
        q?: string;
        sortField?: string;
        sortDescending?: boolean;
    } = {}): Promise<ApiResponse<PagedList<User>>> {
        const {
            page = this.usersCurrentPage,
            pageSize = this.usersPageSize,
            force = false,
            handleError = false,
            q,
            sortField,
            sortDescending = false
        } = options;
        const response: ApiResponse<PagedList<User>> = { data: null, error: null };
        // Prevent duplicate concurrent call with same params
        if (this.isLoadingUsers && !force && page === this.usersCurrentPage && pageSize === this.usersPageSize) {
            return { data: { items: this.pagedUsersSubject.value, pageNumber: this.usersCurrentPage, pageSize: this.usersPageSize, totalCount: this.usersTotalCountSubject.value, hasNext: (this.usersCurrentPage * this.usersPageSize) < this.usersTotalCountSubject.value }, error: null, success: true } as ApiResponse<PagedList<User>>;
        }
        this.isLoadingUsers = true;
        this.cache.loading = true;
        try {
            const params = new URLSearchParams();
            params.set('PageNumber', String(page));
            params.set('PageSize', String(pageSize));
            if (q?.trim()) params.set('q', q.trim());
            if (sortField?.trim()) {
                params.set('sortField', sortField.trim());
                params.set('sortDescending', String(sortDescending));
            }
            const url = `${environment.identityUrl}/user?${params.toString()}`;
            const httpRes = await firstValueFrom(this.core.getResponse(url));
            const rawBody: any = httpRes?.body;
            const paged = rawBody as PagedList<User>;
            const items = (paged?.items || (paged as any)?.data || (Array.isArray(rawBody) ? rawBody : [])) as User[];
            this.pagedUsersSubject.next(items);
            // Prefer pagination metadata from response headers
            const headers = httpRes?.headers;
            const totalHeader = headers?.get('TotalCount') ?? headers?.get('X-Total-Count');
            const sizeHeader = headers?.get('PageSize');
            const currentHeader = headers?.get('CurrentPage');
            const totalPagesHeader = headers?.get('TotalPages');
            const hasNextHeader = headers?.get('HasNext');
            const hasPrevHeader = headers?.get('HasPrevious');
            const xPagination = headers?.get('X-Pagination');

            let totalFromHeader: number | null = null;
            let pageFromHeader: number | null = null;
            let sizeFromHeader: number | null = null;
            let totalPagesFromHeader: number | null = null;
            let hasNextFromHeader: boolean | null = null;
            let hasPrevFromHeader: boolean | null = null;

            if (xPagination) {
                try {
                    const parsed = JSON.parse(xPagination);
                    totalFromHeader = Number(parsed.TotalCount);
                    pageFromHeader = Number(parsed.CurrentPage);
                    sizeFromHeader = Number(parsed.PageSize);
                    totalPagesFromHeader = Number(parsed.TotalPages);
                    hasNextFromHeader = Boolean(parsed.HasNext);
                    hasPrevFromHeader = Boolean(parsed.HasPrevious);
                } catch { /* ignore */ }
            }
            // If individual headers exist, they take precedence over x-pagination
            if (totalHeader !== null) totalFromHeader = Number(totalHeader);
            if (sizeHeader !== null) sizeFromHeader = Number(sizeHeader);
            if (currentHeader !== null) pageFromHeader = Number(currentHeader);
            if (totalPagesHeader !== null) totalPagesFromHeader = Number(totalPagesHeader);
            if (hasNextHeader !== null) hasNextFromHeader = String(hasNextHeader).toLowerCase() === 'true';
            if (hasPrevHeader !== null) hasPrevFromHeader = String(hasPrevHeader).toLowerCase() === 'true';

            if (totalFromHeader !== null || sizeFromHeader !== null || pageFromHeader !== null || totalPagesFromHeader !== null) {
                const total = totalFromHeader ?? 0;
                const pg = pageFromHeader ?? page;
                const psz = sizeFromHeader ?? pageSize;
                const tpages = totalPagesFromHeader ?? (psz ? Math.ceil(total / psz) : 0);
                const hnext = hasNextFromHeader ?? ((pg * psz) < total);
                const hprev = hasPrevFromHeader ?? (pg > 1);

                this.usersTotalCountSubject.next(total);
                this.usersCurrentPage = pg;
                this.usersPageSize = psz;
                response.data = {
                    items,
                    pageNumber: this.usersCurrentPage,
                    pageSize: this.usersPageSize,
                    totalCount: total,
                    hasNext: hnext,
                    hasPrevious: hprev,
                    totalPages: tpages
                };
            } else {
                // Fallback to body-based pagination if headers are missing
                let total = paged?.totalCount ?? (rawBody as any)?.totalCount;
                const toFinite = (v: any): number | null => {
                    const n = typeof v === 'string' ? Number(v) : v;
                    return (typeof n === 'number' && isFinite(n)) ? n : null;
                };
                total = toFinite(total);
                if (total === null) {
                    // Try dedicated count endpoint directly
                    try {
                        const countHttp = await firstValueFrom(this.core.getResponse(`${environment.identityUrl}/user/count`));
                        const body: any = countHttp?.body;
                        let derived: any = body;
                        if (derived && typeof derived === 'object') {
                            derived = derived.data ?? derived.totalCount ?? derived.count ?? derived.total;
                        }
                        const asNum = toFinite(derived);
                        total = asNum !== null ? asNum : null;
                    } catch { /* ignore */ }
                }
                if (total === null) {
                    total = items.length;
                }
                this.usersTotalCountSubject.next(total);
                this.usersCurrentPage = paged?.pageNumber || page;
                this.usersPageSize = paged?.pageSize || pageSize;
                response.data = {
                    items,
                    pageNumber: this.usersCurrentPage,
                    pageSize: this.usersPageSize,
                    totalCount: total,
                    hasNext: (this.usersCurrentPage * this.usersPageSize) < total,
                    hasPrevious: this.usersCurrentPage > 1,
                    totalPages: Math.ceil(total / this.usersPageSize)
                };
            }
            response.success = true;
        } catch (error) {
            response.error = error;
            response.success = false;
            await this.handleError(error, handleError);
        } finally {
            this.cache.loading = false;
            this.isLoadingUsers = false;
        }
        return response;
    }

    /**
     * Attempt to normalize various possible API response shapes into an array of DBCOrder
     * Accepts: Array, { data: [] }, { items: [] }, { results: [] }
     */
    private normalizeOrdersResponse(raw: any): DBCOrder[] {
        if (!raw) return [];
        if (Array.isArray(raw)) return raw as DBCOrder[];
        if (Array.isArray(raw.data)) return raw.data as DBCOrder[];
        if (Array.isArray(raw.items)) return raw.items as DBCOrder[];
        if (Array.isArray(raw.results)) return raw.results as DBCOrder[];
        // If raw is object with a single array property, pick it
        const arrayProp = Object.keys(raw).find(k => Array.isArray((raw as any)[k]));
        if (arrayProp) return (raw as any)[arrayProp] as DBCOrder[];
        return [];
    }
    //attach user details by matching userId in orders with user details
    private attachUserDetailsToOrders(orders: DBCOrder[], users: User[]): DBCOrder[] {
        let orderList = [];
        const userMap = new Map(users.map(user => [user.id, user]));
        if (!userMap.size) {
            this.refreshUsers();
        }
        orderList =  orders.map(order => {
            const user = userMap.get(order.userId);
            return {
                ...order,
                user: user ? { id: user.id, userDetails : user } : null
            };
        });
        // for (const order of orderList) {
        //     order.productDetails = this.attachProductDetailsToOrders([order]);
        // }
        this.utils.logger.info("available products", this.dbcOrderList);
        return orderList;
    }
    // Map QR details to a flat property for UI (e.g., thumbnails)
    private attachQrDetailsToOrders(orders: DBCOrder[]): DBCOrder[] {
        return orders.map(o => {
            const anyOrder: any = o as any;
            const existingQr = anyOrder.qrCode || anyOrder.qr || null;
            const fileUrl = (existingQr && (existingQr.fileUrl || existingQr.url)) || anyOrder.qrFileUrl || '';
            const orderId = anyOrder.id || anyOrder.orderId || '';
            const nowIso = new Date().toISOString();

            // Build normalized qrCode object matching backend schema
            const qrCode = {
                id: existingQr?.id || '',
                orderId: existingQr?.orderId || orderId,
                fileUrl: fileUrl,
                side: existingQr?.side || 'QR',
                isTemporary: existingQr?.isTemporary ?? false,
                createdDate: existingQr?.createdDate || anyOrder.createdDate || nowIso,
                modifiedDate: existingQr?.modifiedDate || anyOrder.modifiedDate || nowIso
            };

            return { ...anyOrder, qrCode, qrFileUrl: qrCode.fileUrl };
        });
    }
    //attach product details
    private async attachProductDetailsToOrders(orders: DBCOrder[], forceProducts: boolean = false): Promise<DBCOrder[]> {
        const products = await this.ensureProductsLoaded(forceProducts);
        if (!products.length) return orders;
        const productMap = new Map(products.map((p: any) => [p.id, p]));
        return orders.map(o => ({
            ...o,
            product: productMap.get((o as any).productId) || null
        }));
    }

    /** Force products reload and reattach to cached orders */
    public async refreshProductsAndReattach(): Promise<void> {
        await this.ensureProductsLoaded(true);
        if (this.dbcOrderList.length) {
            this.dbcOrderList = await this.attachProductDetailsToOrders(this.dbcOrderList, false);
            this.dbcOrdersSubject.next(this.dbcOrderList);
        }
    }

    // ============================================================================
    // DYNAMIC MENU
    // ============================================================================

    /**
     * Primary method: Extract admin panel routes and convert them to menu items
     *
     * This method automatically finds admin routes in the Angular Router configuration
     * and converts them to PrimeNG MegaMenuItem format for use in navigation menus.
     *
     * @param baseRoutePath The base route path for admin panel (default: '/admin/panel')
     * @returns Array of MegaMenuItem objects ready for PrimeNG MegaMenu
     *
     * @example
     * -Basic usage
     * const menuItems = this.adminPanelService.getAdminMenuItems();
     *
     * @example
     * -With custom base path
     * const menuItems = this.adminPanelService.getAdminMenuItems('/custom/admin/path');
     */
    public getAdminMenuItems(baseRoutePath: string = '/admin/panel'): MegaMenuItem[] {
        const adminRoutes = this.getAdminRoutes();
        return this.convertRoutesToMenuItems(adminRoutes, baseRoutePath);
    }

    public getMenuItemsFromRouteConfig(routeConfig: Route[]): MegaMenuItem[] {
        const adminRouteConfigs = this.extractRouteConfigs(routeConfig);
        return this.convertRoutesToMenuItems(adminRouteConfigs, '/admin/panel');
    }
    public updateOrderStatus(order:any) {
        return this.updateOrderStatusById(order?.id, order?.status, order);
    }

    /** Permanently deletes a user through the server-owned admin cleanup workflow. */
    public async deleteUser(
        user: User,
        reason: string,
        options: { handleError?: boolean } = {}
    ): Promise<ApiResponse<UserDeletionResult>> {
        const { handleError = true } = options;
        const response: ApiResponse<UserDeletionResult> = { data: null, error: null };

        if (!user?.id) {
            response.error = 'Missing user ID';
            response.success = false;
            return response;
        }

        if (!reason?.trim()) {
            response.error = 'A deletion reason is required';
            response.success = false;
            return response;
        }

        this.cache.loading = true;
        try {
            const url = `${environment.billingServiceUrl}/admin/users/${encodeURIComponent(user.id)}/delete`;
            const result = await firstValueFrom(this.core.post(url, {
                reason: reason.trim()
            })) as UserDeletionResult & { succeeded: boolean };

            if (result.succeeded) {
                const source = result.stripe.status === 'deleted'
                    ? 'billing-and-identity'
                    : result.stripe.status === 'not-requested'
                        ? 'identity'
                        : 'identity-fallback';
                response.data = { ...result, source };
                response.success = true;
                this.userList = this.userList.filter(cachedUser => cachedUser.id !== user.id);
                this.usersSubject.next([...this.userList]);
                this.pagedUsersSubject.next(
                    this.pagedUsersSubject.value.filter(cachedUser => cachedUser.id !== user.id)
                );
                this.usersTotalCountSubject.next(Math.max(this.usersTotalCountSubject.value - 1, 0));
                this.userCount = Math.max(this.userCount - 1, 0);
                return response;
            }

            response.data = {
                ...result,
                source: result.stripe.status === 'deleted' ? 'billing-and-identity' : 'identity-fallback'
            };
            const failureDetail = result.identity.detail || 'The local identity record could not be removed.';
            response.error = `${failureDetail} Reconciliation reference: ${result.operationId}.`;
            response.success = false;
            if (handleError) {
                this.utils.errorMessage(MessageType.TOAST, 'Delete Failed', String(response.error));
            }
        } catch (error: any) {
            this.utils.logger.error('deleteUser failed', { userId: user.id, error });
            response.error = error;
            response.success = false;
            await this.handleError(error, handleError);
        } finally {
            this.cache.loading = false;
        }

        return response;
    }

    public async getUserDeletionOperations(options: {
        page?: number;
        pageSize?: number;
        status?: string;
        q?: string;
        handleError?: boolean;
    } = {}): Promise<ApiResponse<UserDeletionOperationsPage>> {
        const response: ApiResponse<UserDeletionOperationsPage> = { data: null, error: null };
        const params = new URLSearchParams();
        params.set('page', String(options.page ?? 1));
        params.set('pageSize', String(options.pageSize ?? 25));
        if (options.status?.trim()) params.set('status', options.status.trim());
        if (options.q?.trim()) params.set('q', options.q.trim());

        try {
            response.data = await firstValueFrom(
                this.core.get(`${environment.billingServiceUrl}/admin/users/deletions?${params.toString()}`)
            ) as UserDeletionOperationsPage;
            response.totalCount = response.data.totalCount;
            response.success = true;
        } catch (error) {
            response.error = error;
            response.success = false;
            await this.handleError(error, options.handleError ?? true);
        }

        return response;
    }

    public async retryUserDeletionOperation(
        operationId: string,
        reason: string,
        options: { handleError?: boolean } = {}
    ): Promise<ApiResponse<UserDeletionResult>> {
        const response: ApiResponse<UserDeletionResult> = { data: null, error: null };
        if (!operationId?.trim() || !reason?.trim()) {
            response.error = 'An operation and retry reason are required';
            response.success = false;
            return response;
        }

        try {
            response.data = await firstValueFrom(this.core.post(
                `${environment.billingServiceUrl}/admin/users/deletions/${encodeURIComponent(operationId)}/retry`,
                { reason: reason.trim() }
            )) as UserDeletionResult;
            response.success = true;
        } catch (error) {
            response.error = error;
            response.success = false;
            await this.handleError(error, options.handleError ?? true);
        }

        return response;
    }

    public async updateOrderStatusById(orderId: string, newStatus: string, original?: DBCOrder): Promise<ApiResponse<DBCOrder>> {
        const response: ApiResponse<DBCOrder> = { data: null, error: null };
        if (!orderId) {
            response.error = 'Missing order id';
            return response;
        }
        const base: Partial<DBCOrder> = original ? { ...original } as DBCOrder : (this.dbcOrderList.find(o => o.id === orderId) as DBCOrder | undefined);
        if (!base) {
            response.error = 'Order not found in cache';
            return response;
        }

        const payload  = {
            id: base.id,
            userId: base.userId,
            totalAmount: base.totalAmount,
            quantity: base.quantity,
            productId: base.productId,
            status: newStatus || base.status,
            projectId: base.projectId,
            createdDate: base.createdDate,
            modifiedDate: new Date().toISOString(),
        };
        this.cache.loading = true;
        try {
            const updated = await firstValueFrom(this.core.bulkPut(`${environment.billingServiceUrl}/Order`, payload));
            // Merge update into local caches (paged + full lists); clear transient UI flags like 'updating'
            this.dbcOrderList = this.dbcOrderList.map(o => o.id === orderId ? { ...o, ...payload, updating: false } : o);
            this.dbcOrdersSubject.next(this.dbcOrderList);
            // Also update paged stream if present
            if (this.pagedOrdersSubject.value?.length) {
                const paged = this.pagedOrdersSubject.value.map(o => o.id === orderId ? { ...o, ...payload, updating: false } : o);
                this.pagedOrdersSubject.next(paged);
            }
            response.data = (updated as any) || payload;
            response.success = true;
        } catch (error) {
            response.error = error;
            response.success = false;
            await this.handleError(error, true);
        } finally {
            this.cache.loading = false;
        }
        return response;
    }
   //Get the admin panel routes configuration
    private getAdminRoutes(): AdminRouteConfig[] {
        const routes = this.router.config;

        // Find the admin panel route - you might need to adjust this path based on your routing structure
        const adminRoute = this.findRouteByPath(routes, 'admin/panel');

        if (adminRoute && adminRoute.children) {
            return this.extractRouteConfigs(adminRoute.children);
        }

        // Fallback: return hardcoded route configs if dynamic extraction fails
        return this.getFallbackRouteConfigs();
    }

     //Find a route by its path in the routes array
    private findRouteByPath(routes: Route[], targetPath: string): Route | undefined {
        for (const route of routes) {
            if (route.path === targetPath) {
                return route;
            }
            if (route.children) {
                const found = this.findRouteByPath(route.children, targetPath);
                if (found) return found;
            }
        }
        return undefined;
    }

    //Extract route configurations from Route array
    private extractRouteConfigs(routes: Route[]): AdminRouteConfig[] {
        return routes
            .filter(route => route.data && route.data['breadcrumb']) // Only include routes with breadcrumb data
            .map(route => ({
                path: route.path || '',
                breadcrumb: route.data!['breadcrumb'],
                page: route.data!['page'] || route.path || '',
                icon: this.getIconForRoute(route.data!['page'] || route.path || '')
            }));
    }

     //Convert route configurations to PrimeNG MegaMenuItem format
    private convertRoutesToMenuItems(routeConfigs: AdminRouteConfig[], baseRoutePath: string): MegaMenuItem[] {
        return routeConfigs.map(config => {
            const isRootRoute = config.path === '';
            const routerLink = isRootRoute ? [baseRoutePath] : [`${baseRoutePath}/${config.path}`];

            return {
                label: config.breadcrumb,
                icon: config.icon || 'pi pi-circle',
                routerLink: routerLink,
                routerLinkActiveOptions: { exact: isRootRoute }, // Exact match only for root route
                styleClass: 'admin-menu-item'
            };
        });
    }

    /**
     * Get appropriate icon based on the page/route name
     *
     * This method maps page identifiers to PrimeNG icons. Add new mappings here
     * when adding new admin routes that need specific icons.
     *
     * @param page The page identifier from route data
     * @returns PrimeNG icon class name
     *
     * @example
     * -To add a new icon mapping:
     * const iconMap = {
     *   'new-feature': 'pi pi-star',
     * - ... existing mappings
     * };
     */
    private getIconForRoute(page: string): string {
        const iconMap: { [key: string]: string } = {
            'admin-dashboard': 'pi pi-microsoft',
            'dashboard': 'pi pi-microsoft',
            'users': 'pi pi-users',
            'deletion-operations': 'pi pi-history',
            'organizations': 'pi pi-building',
            'dbc-orders': 'pi pi-shopping-cart',
            'orders': 'pi pi-shopping-cart',
            'settings': 'pi pi-cog',
            'reports': 'pi pi-chart-bar',
            'analytics': 'pi pi-chart-line'
        };

        return iconMap[page] || 'pi pi-circle';
    }

    //Fallback route configurations in case dynamic extraction fails
    private getFallbackRouteConfigs(): AdminRouteConfig[] {
        return [
            {
                path: '',
                breadcrumb: 'Dashboard',
                page: 'admin-dashboard',
                icon: 'pi pi-microsoft'
            },
            {
                path: 'users',
                breadcrumb: 'Users',
                page: 'users',
                icon: 'pi pi-users'
            },
            {
                path: 'organizations',
                breadcrumb: 'Organizations',
                page: 'organizations',
                icon: 'pi pi-building'
            },
            {
                path: 'dbc-orders',
                breadcrumb: 'DBC Orders',
                page: 'dbc-orders',
                icon: 'pi pi-shopping-cart'
            }
        ];
    }

    private normalizeOrganizations(raw: unknown): AdminOrganization[] {
        const list = Array.isArray(raw)
            ? raw
            : Array.isArray((raw as { data?: unknown[] })?.data)
                ? (raw as { data: AdminOrganization[] }).data
                : Array.isArray((raw as { items?: unknown[] })?.items)
                    ? (raw as { items: AdminOrganization[] }).items
                    : (raw as { data?: { items?: unknown[] } })?.data?.items && Array.isArray((raw as { data: { items: unknown[] } }).data.items)
                        ? (raw as { data: { items: AdminOrganization[] } }).data.items
                    : [];

        return (list as AdminOrganization[])
            .map(org => this.normalizeOrganization(org))
            .filter((org): org is AdminOrganization => org != null)
            .sort((left, right) => (left?.name || '').localeCompare(right?.name || ''));
    }

    private normalizeOrganization(org: AdminOrganization | Record<string, unknown>): AdminOrganization | null {
        if (!org || typeof org !== 'object') {
            return null;
        }

        const source = org as Record<string, unknown>;
        const parsedId = this.parseNumericValue(source.id as number | string | null | undefined, source.organizationId as number | string | null | undefined, source.orgId as number | string | null | undefined, Number.NaN);
        if (Number.isNaN(parsedId)) {
            return null;
        }

        const normalized: AdminOrganization = {
            ...(source as unknown as AdminOrganization),
            id: parsedId,
            isDeleted: this.parseBooleanValue(source.isDeleted, source.IsDeleted, false),
            deletedDate: this.parseStringOrNullValue(source.deletedDate, source.DeletedDate),
            deletedByUserId: this.parseStringOrNullValue(source.deletedByUserId, source.DeletedByUserId)
        };

        return normalized;
    }

    private normalizePagedOrganizations(raw: unknown, fallbackPage: number, fallbackPageSize: number): PagedList<AdminOrganization> {
        const root = raw as any;
        const data = root?.data ?? root;

        const items = this.normalizeOrganizations(
            Array.isArray(data?.items)
                ? data.items
                : Array.isArray(root?.items)
                    ? root.items
                    : Array.isArray(data)
                        ? data
                        : Array.isArray(root)
                            ? root
                            : []
        );

        const totalCount = this.parseNumericValue(
            data?.totalCount,
            root?.totalCount,
            items.length
        );
        const pageNumber = this.parseNumericValue(
            data?.pageNumber,
            root?.pageNumber,
            fallbackPage
        );
        const pageSize = this.parseNumericValue(
            data?.pageSize,
            root?.pageSize,
            fallbackPageSize
        );
        const totalPages = this.parseNumericValue(
            data?.totalPages,
            root?.totalPages,
            Math.ceil(totalCount / pageSize)
        );

        return {
            items,
            pageNumber,
            pageSize,
            totalCount,
            totalPages,
            hasNext: pageNumber < totalPages,
            hasPrevious: pageNumber > 1
        };
    }

    private parseNumericValue(...values: Array<number | string | null | undefined>): number {
        for (const value of values) {
            if (typeof value === 'number' && !Number.isNaN(value)) {
                return value;
            }

            if (typeof value === 'string') {
                const parsedValue = Number(value);
                if (!Number.isNaN(parsedValue)) {
                    return parsedValue;
                }
            }
        }

        return 0;
    }

    private parseBooleanValue(...values: unknown[]): boolean {
        for (const value of values) {
            if (typeof value === 'boolean') {
                return value;
            }

            if (typeof value === 'string') {
                const normalized = value.trim().toLowerCase();
                if (normalized === 'true') {
                    return true;
                }

                if (normalized === 'false') {
                    return false;
                }
            }
        }

        return false;
    }

    private parseStringOrNullValue(...values: unknown[]): string | null {
        for (const value of values) {
            if (value == null) {
                continue;
            }

            if (typeof value === 'string') {
                return value;
            }

            return String(value);
        }

        return null;
    }

    private normalizeOrganizationMembers(raw: unknown): { items: AdminOrganizationMember[]; totalCount: number | null } {
        const root = raw as any;
        const data = root?.data ?? root;

        let members: unknown[] = [];
        if (Array.isArray(data)) {
            members = data;
        } else if (Array.isArray(data?.members)) {
            members = data.members;
        } else if (Array.isArray(data?.items)) {
            members = data.items;
        }

        const normalizedMembers = (members as AdminOrganizationMember[]).map(member => ({
            ...member,
            name: member.name || [member['firstName'], member['lastName']].filter(Boolean).join(' ').trim() || member.email || 'Unknown member'
        }));

        let totalCount: number | null = null;
        if (typeof root?.totalCount === 'number') {
            totalCount = root.totalCount;
        } else if (typeof root?.data?.totalCount === 'number') {
            totalCount = root.data.totalCount;
        } else if (typeof root?.data?.totalCount === 'string') {
            totalCount = Number(root.data.totalCount);
        }

        return {
            items: normalizedMembers,
            totalCount
        };
    }

    private normalizeOrganizationAuditLogs(raw: unknown): { items: AdminOrganizationAuditLog[]; totalCount: number | null } {
        const root = raw as Record<string, unknown> | null;
        const data = (root?.['data'] as Record<string, unknown> | unknown[] | undefined) ?? raw;

        let itemsSource: unknown[] = [];
        if (Array.isArray(data)) {
            itemsSource = data;
        } else if (Array.isArray((data as Record<string, unknown> | undefined)?.['items'])) {
            itemsSource = (data as Record<string, unknown>)['items'] as unknown[];
        } else if (Array.isArray((root?.['items'] as unknown[] | undefined))) {
            itemsSource = root?.['items'] as unknown[];
        }

        const normalizedItems = itemsSource
            .map(item => this.normalizeOrganizationAuditLog(item))
            .filter((item): item is AdminOrganizationAuditLog => item != null);

        const totalCount = this.tryParseNumber(
            (data as Record<string, unknown> | undefined)?.['total'],
            (data as Record<string, unknown> | undefined)?.['totalCount'],
            root?.['total'],
            root?.['totalCount']
        );

        return {
            items: normalizedItems,
            totalCount
        };
    }

    private normalizeOrganizationAuditLog(raw: unknown): AdminOrganizationAuditLog | null {
        if (!raw || typeof raw !== 'object') {
            return null;
        }

        const source = raw as Record<string, unknown>;
        const idValue = source['id'];
        const id = idValue == null ? '' : String(idValue);

        if (!id) {
            return null;
        }

        return {
            id,
            createdDate: this.tryParseString(source['createdDate']),
            actorUserId: this.tryParseString(source['actorUserId']),
            actorName: this.tryParseString(source['actorName']),
            action: this.tryParseString(source['action']),
            summary: this.tryParseString(source['summary']),
            targetType: this.tryParseNullableString(source['targetType']),
            targetId: this.tryParseNullableString(source['targetId']),
            ipAddress: this.tryParseNullableString(source['ipAddress']),
            userAgent: this.tryParseNullableString(source['userAgent'])
        };
    }

    private normalizeOrganizationAuditDetail(raw: unknown, fallbackAuditId: string): AdminOrganizationAuditDetail {
        const root = raw as Record<string, unknown> | null;
        const source = (root?.['data'] as Record<string, unknown> | undefined) ?? root ?? {};
        const idValue = source['id'] ?? fallbackAuditId;

        return {
            id: idValue == null ? fallbackAuditId : String(idValue),
            createdDate: this.tryParseString(source['createdDate']),
            actorUserId: this.tryParseString(source['actorUserId']),
            actorName: this.tryParseString(source['actorName']),
            action: this.tryParseString(source['action']),
            summary: this.tryParseString(source['summary']),
            targetType: this.tryParseNullableString(source['targetType']),
            targetId: this.tryParseNullableString(source['targetId']),
            ipAddress: this.tryParseNullableString(source['ipAddress']),
            userAgent: this.tryParseNullableString(source['userAgent']),
            metadata: this.normalizeAuditMetadata(source['metadata']),
            metadataJson: this.tryParseNullableString(source['metadataJson'])
        };
    }

    private normalizeAuditMetadata(rawMetadata: unknown): Record<string, unknown> | null {
        if (!rawMetadata || typeof rawMetadata !== 'object' || Array.isArray(rawMetadata)) {
            return null;
        }

        return rawMetadata as Record<string, unknown>;
    }

    private tryParseNumber(...values: unknown[]): number | null {
        for (const value of values) {
            if (typeof value === 'number' && !Number.isNaN(value)) {
                return value;
            }

            if (typeof value === 'string') {
                const parsed = Number(value);
                if (!Number.isNaN(parsed)) {
                    return parsed;
                }
            }
        }

        return null;
    }

    private tryParseString(value: unknown): string | undefined {
        if (value == null) {
            return undefined;
        }

        if (typeof value === 'string') {
            return value;
        }

        return String(value);
    }

    private tryParseNullableString(value: unknown): string | null | undefined {
        if (value === null) {
            return null;
        }

        if (value === undefined) {
            return undefined;
        }

        if (typeof value === 'string') {
            return value;
        }

        return String(value);
    }

    // ============================================================================
    // ERROR HANDLING
    // ============================================================================

    private handleError(error: any, handleError : boolean = false) {
        this.utils.logger.error(error);
        if (typeof error.error === 'object' && (error.error.code === 1010 || error.error.code === 1007 || error.error.code === 1008 || error.error.code === 1009 || error.error.code === 9208)) {
          this.utils.errorMessage(MessageType.ALERT, error.error.title);
        } else {
          if (handleError) {
            this.utils.errorMessage(MessageType.ALERT, 'There was an error processing your request. Please try again later.');
          }
        }
      }
    private requireCompletedBillingOperation(response: unknown): void {
        const operation = response as { status?: string; error?: string };
        if (operation?.status !== 'completed') {
            throw new Error(operation?.error ?? 'Organization billing cleanup is pending. Refresh before retrying.');
        }
    }
}
